import { ThreadPrimitive, useAui, useAuiState, useThreadViewportStore } from '@assistant-ui/react';
import AssistantChatRuntime, { useChatRuntime } from 'components/Chat/AssistantChatRuntime';
import ChatComposer from 'components/Chat/ChatComposer';
import { getMessageText, hasAssistantStreamingOutput } from 'components/Chat/chatHelpers';
import ChatMessages from 'components/Chat/ChatMessages';
import ChatPromptSuggestions from 'components/Chat/ChatPromptSuggestions';
import { useWebsocket } from 'contexts/websocket';
import useBottomScrollAnchor from 'hooks/useBottomScrollAnchor';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Icon, Loading, Stack, styled, Text, Tooltip } from 'shell/components';

const CHAT_PANEL_SPACING = '20px';
const THREAD_BOTTOM_STICK_THRESHOLD_PX = 80;
const CHAT_BG_COLOR = '#FCFCFC';

const BaseChatPanel = ({
    chat,
    fileKey,
    hasOlderMessages,
    isBootstrapping,
    isLoadingOlderMessages,
    isOpen,
    onLoadOlderMessages,
    suggestions,
    showSuggestions,
    viewModel,
    onBack,
    onSuggestionSelected,
    onUserMessageCreated,
    onAssistantMessageCreated,
}) => {
    const { isConnected, onMessage, sendMessage } = useWebsocket();
    const threadViewportRef = useRef(null);
    const [shouldScrollToBottomOnInitialize, setShouldScrollToBottomOnInitialize] = useState(isOpen);
    const previousIsOpenRef = useRef(isOpen);

    useEffect(() => {
        const didOpen = !previousIsOpenRef.current && isOpen;
        const didClose = previousIsOpenRef.current && !isOpen;

        if (didOpen) {
            setShouldScrollToBottomOnInitialize(true);
        }

        if (didClose) {
            setShouldScrollToBottomOnInitialize(false);
        }

        previousIsOpenRef.current = isOpen;
    }, [isOpen]);

    useEffect(() => {
        if (!shouldScrollToBottomOnInitialize) return;

        const frameId = requestAnimationFrame(() => {
            setShouldScrollToBottomOnInitialize(false);
        });

        return () => {
            cancelAnimationFrame(frameId);
        };
    }, [shouldScrollToBottomOnInitialize]);

    if (isBootstrapping) {
        return <Loading />;
    }

    return (
        <AssistantChatRuntime
            chat={chat}
            fileKey={fileKey}
            isConnected={isConnected}
            onAssistantMessageCreated={onAssistantMessageCreated}
            onMessage={onMessage}
            onUserMessageCreated={onUserMessageCreated}
            sendMessage={sendMessage}
        >
            <BaseChatPanelBody
                hasOlderMessages={hasOlderMessages}
                isConnected={isConnected}
                isLoadingOlderMessages={isLoadingOlderMessages}
                onLoadOlderMessages={onLoadOlderMessages}
                suggestions={suggestions}
                showSuggestions={showSuggestions}
                viewModel={viewModel}
                onBack={onBack}
                onSuggestionSelected={onSuggestionSelected}
                shouldScrollToBottomOnInitialize={shouldScrollToBottomOnInitialize}
                threadViewportRef={threadViewportRef}
            />
        </AssistantChatRuntime>
    );
};

const BaseChatPanelBody = ({
    hasOlderMessages,
    isConnected,
    isLoadingOlderMessages,
    onLoadOlderMessages,
    suggestions,
    showSuggestions,
    shouldScrollToBottomOnInitialize,
    threadViewportRef,
    viewModel,
    onBack,
    onSuggestionSelected,
}) => {
    const aui = useAui();
    const threadViewportStore = useThreadViewportStore();
    const composerText = useAuiState((state) => state.composer.text);
    const messages = useAuiState((state) => state.thread.messages);
    const isRunning = useAuiState((state) => state.thread.isRunning);
    const { chatError, clearChatError, isRetrying, manualAssistantDraft, retryLastRequest } = useChatRuntime();
    const headerTitle = viewModel?.header?.title;
    const headerInfoTooltip = viewModel?.header?.infoTooltip;
    const hasStreamingOutput = hasAssistantStreamingOutput({ isRunning, messages });
    const hasBlockingChatError = Boolean(chatError?.actionType);
    const hasTriggeredTopLoadRef = useRef(false);
    const pendingScrollRestoreRef = useRef(null);
    const suggestionsOverlayRef = useRef(null);
    const [suggestionsOverlayHeight, setSuggestionsOverlayHeight] = useState(0);
    const [hasMeasuredSuggestionsOverlay, setHasMeasuredSuggestionsOverlay] = useState(false);
    const [showTopScrollGradient, setShowTopScrollGradient] = useState(false);
    const lastMessage = messages[messages.length - 1];
    const bottomScrollAnchorKey = `${messages.length}:${lastMessage?.id || ''}:${getMessageText(lastMessage?.content || []).length}:${manualAssistantDraft.length}`;

    const { updateBottomScrollAnchor } = useBottomScrollAnchor({
        containerRef: threadViewportRef,
        contentKey: bottomScrollAnchorKey,
        isActive: isRunning || Boolean(manualAssistantDraft),
        isPaused: isLoadingOlderMessages,
        thresholdPx: THREAD_BOTTOM_STICK_THRESHOLD_PX,
    });

    const scrollThreadToBottom = () => {
        threadViewportStore.getState().scrollToBottom({ behavior: 'smooth' });
    };

    const sendComposerMessage = () => {
        if (isRunning || isRetrying || !isConnected) return;

        clearChatError();
        scrollThreadToBottom();
        aui.composer().send();
    };

    const sendSuggestion = (text) => {
        onSuggestionSelected?.();
        aui.composer().setText(text);
        sendComposerMessage();
    };

    useLayoutEffect(() => {
        if (!pendingScrollRestoreRef.current || !threadViewportRef.current || isLoadingOlderMessages) return;

        const { previousScrollHeight, previousScrollTop } = pendingScrollRestoreRef.current;
        const nextScrollHeight = threadViewportRef.current.scrollHeight;

        threadViewportRef.current.scrollTop = previousScrollTop + (nextScrollHeight - previousScrollHeight);
        pendingScrollRestoreRef.current = null;
    }, [isLoadingOlderMessages, messages.length, threadViewportRef]);

    useLayoutEffect(() => {
        const element = suggestionsOverlayRef.current;

        if (!showSuggestions || suggestions.length === 0 || !element) {
            setSuggestionsOverlayHeight(0);
            setHasMeasuredSuggestionsOverlay(false);
            return;
        }

        const resizeObserver = new ResizeObserver(([entry]) => {
            const nextHeight = entry.target.offsetHeight || 0;
            setSuggestionsOverlayHeight((currentHeight) => Math.max(currentHeight, nextHeight));
            setHasMeasuredSuggestionsOverlay(true);
        });

        resizeObserver.observe(element);

        return () => {
            resizeObserver.disconnect();
        };
    }, [showSuggestions, suggestions.length]);

    const shouldDelayThreadContent = showSuggestions && suggestions.length > 0 && !hasMeasuredSuggestionsOverlay;

    useEffect(() => {
        const element = threadViewportRef.current;
        if (!element) return;

        setShowTopScrollGradient(element.scrollTop > 0);
    }, [messages.length, shouldDelayThreadContent, threadViewportRef]);

    const onThreadScroll = async (event) => {
        updateBottomScrollAnchor(event.currentTarget);
        setShowTopScrollGradient(event.currentTarget.scrollTop > 0);

        if (event.currentTarget.scrollTop > 48) {
            hasTriggeredTopLoadRef.current = false;
            return;
        }

        if (!hasOlderMessages || isLoadingOlderMessages || !onLoadOlderMessages) return;
        if (isRunning || isRetrying) return;
        if (hasTriggeredTopLoadRef.current) return;

        hasTriggeredTopLoadRef.current = true;
        pendingScrollRestoreRef.current = {
            previousScrollHeight: event.currentTarget.scrollHeight,
            previousScrollTop: event.currentTarget.scrollTop,
        };
        await onLoadOlderMessages();
    };

    return (
        <RootStack>
            <HeaderRow>
                <BackButton startIcon={<Icon kind={'arrowLeft'} />} variant={'borderless'} onClick={onBack}>
                    Back
                </BackButton>
                <CenteredTitleRow alignItems={'center'} direction={'row'} justifyContent={'center'} spacing={1}>
                    <Text component={'h2'} weight={'semibold'}>
                        {headerTitle}
                    </Text>
                    {headerInfoTooltip ? <Tooltip content={headerInfoTooltip} type={'info'} /> : null}
                </CenteredTitleRow>
            </HeaderRow>

            <ThreadViewportShell hasTopScrollGradient={showTopScrollGradient ? 1 : 0}>
                <ThreadViewport
                    ref={threadViewportRef}
                    turnAnchor='bottom'
                    autoScroll={true}
                    scrollToBottomOnRunStart={false}
                    scrollToBottomOnInitialize={shouldScrollToBottomOnInitialize}
                    scrollToBottomOnThreadSwitch={false}
                    onScroll={onThreadScroll}
                >
                    {isLoadingOlderMessages ? (
                        <ViewportTopStatus
                            alignItems={'center'}
                            direction={'row'}
                            justifyContent={'center'}
                            spacing={1}
                        >
                            <ViewportLoadingSpinner aria-hidden={'true'} />
                            <ViewportLoadingText variant={'p3'}>Loading older messages...</ViewportLoadingText>
                        </ViewportTopStatus>
                    ) : null}
                    {shouldDelayThreadContent ? (
                        <Loading />
                    ) : (
                        <ChatMessages
                            bottomInset={suggestionsOverlayHeight}
                            hasLoadingPlaceholder={
                                (isRunning && !hasStreamingOutput) || (isRetrying && !manualAssistantDraft)
                            }
                            isRunning={isRunning}
                            messages={messages}
                        />
                    )}
                </ThreadViewport>
                {showSuggestions && suggestions.length > 0 ? (
                    <SuggestionsOverlay ref={suggestionsOverlayRef}>
                        <SuggestionsContainer>
                            <ChatPromptSuggestions
                                disabled={hasBlockingChatError || isRunning || isRetrying || !isConnected}
                                suggestions={suggestions}
                                onSelect={sendSuggestion}
                            />
                        </SuggestionsContainer>
                    </SuggestionsOverlay>
                ) : null}
            </ThreadViewportShell>
            <ComposerDock>
                {chatError ? (
                    <ChatErrorBanner spacing={5}>
                        <Text color={'error'}>{chatError.message}</Text>
                        {chatError.actionType ? (
                            <Button
                                startIcon={chatError.icon ? <Icon kind={chatError.icon} /> : null}
                                variant={'secondary'}
                                onClick={() => {
                                    clearChatError();
                                    if (chatError.actionType === 'refresh') {
                                        window.location.reload();
                                        return;
                                    }

                                    retryLastRequest?.();
                                }}
                            >
                                {chatError.actionLabel}
                            </Button>
                        ) : null}
                    </ChatErrorBanner>
                ) : null}
                <ChatComposer
                    disabled={hasBlockingChatError || isRunning || isRetrying || !isConnected}
                    disabledTooltip={isRunning ? 'Please wait while the AI is responding.' : ''}
                    placeholder={'Ask a question about this feedback'}
                    value={composerText}
                    onChange={(value) => aui.composer().setText(value)}
                    onSend={sendComposerMessage}
                />
            </ComposerDock>
        </RootStack>
    );
};

const RootStack = styled(Stack)(() => {
    return {
        flex: 1,
        padding: CHAT_PANEL_SPACING,
        background: CHAT_BG_COLOR,
        minHeight: 0,
        fontSize: '14px',

        '& .MuiButton-root': {
            fontSize: '14px',
        },
        '& .MuiInputBase-input, & textarea': {
            fontSize: '14px',
        },
    };
});

const HeaderRow = styled(Stack)(({ theme }) => {
    return {
        position: 'relative',
        minHeight: theme.spacing(5),
        justifyContent: 'center',
        paddingBottom: theme.spacing(3),
    };
});

const BackButton = styled(Button)(() => {
    return {
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 1,
    };
});

const CenteredTitleRow = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(0, 10),
        flexWrap: 'nowrap',
    };
});

const ThreadViewport = styled(ThreadPrimitive.Viewport)(({ theme }) => {
    return {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(3),
        marginRight: `-${CHAT_PANEL_SPACING}`,
        paddingRight: CHAT_PANEL_SPACING,
    };
});

const ThreadViewportShell = styled(Stack, {
    shouldForwardProp: (prop) => prop !== 'hasTopScrollGradient',
})(({ hasTopScrollGradient }) => {
    return {
        position: 'relative',
        flex: 1,
        minHeight: 0,

        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '48px',
            pointerEvents: 'none',
            opacity: hasTopScrollGradient ? 1 : 0,
            transition: 'opacity 160ms ease',
            background: `linear-gradient(180deg, ${CHAT_BG_COLOR} 0%, rgba(252, 252, 252, 0) 100%)`,
            zIndex: 1,
        },
    };
});

const ViewportTopStatus = styled(Stack)(({ theme }) => {
    return {
        position: 'sticky',
        top: 4,
        zIndex: 1,
        gap: theme.spacing(2),
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
        background: CHAT_BG_COLOR,
    };
});

const ViewportLoadingText = styled(Text)(({ theme }) => {
    return {
        color: theme.palette.ensightful.primary[500],
    };
});

const ViewportLoadingSpinner = styled('span')(({ theme }) => {
    return {
        width: theme.spacing(2),
        height: theme.spacing(2),
        borderRadius: '50%',
        border: `2px solid ${theme.palette.ensightful.primary[100]}`,
        borderTopColor: theme.palette.ensightful.primary[500],
        animation: 'chat-loading-spin 0.8s linear infinite',

        '@keyframes chat-loading-spin': {
            '0%': {
                transform: 'rotate(0deg)',
            },
            '100%': {
                transform: 'rotate(360deg)',
            },
        },
    };
});

const ComposerDock = styled(Stack)(({ theme }) => {
    return {
        gap: theme.spacing(5),
    };
});

const ChatErrorBanner = styled(Stack)(({ theme }) => {
    return {
        alignItems: 'flex-start',
        padding: theme.spacing(2),
    };
});

const SuggestionsContainer = styled(Stack)(() => {
    return {
        width: '100%',
        alignItems: 'flex-end',
    };
});

const SuggestionsOverlay = styled('div')(({ theme }) => {
    return {
        position: 'absolute',
        right: CHAT_PANEL_SPACING,
        bottom: 0,
        left: 0,
        paddingLeft: CHAT_PANEL_SPACING,
        paddingBottom: theme.spacing(2.5),
        pointerEvents: 'none',
        '& > *': {
            pointerEvents: 'auto',
        },
    };
});

export default BaseChatPanel;
